import { useMutation } from '@apollo/client/react'
import {
  ActiveScanCenterCodeEnum,
  defaultAddress,
  getReturnsCSV,
  getReturnsCSVFileName,
  type Return,
} from '@getcaya/scan-center'
import {
  Button,
  notification,
  Table,
  type TableColumnProps,
  type TableProps,
} from 'antd'
import dayjs from 'dayjs'
import { saveAs } from 'file-saver'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ContainerType,
  RequestedReturnsDocument,
  type RequestedReturnsQuery,
  SendReturnDocument,
  SendReturnsWithTrackingIdsDocument,
} from '~app/graph/generated/graphql'
import {
  type PaginatedReturn,
  type Document as ReturnDocument,
  usePaginatedReturns,
} from '~app/graph/return'
import i18n from '~app/i18n/i18n'
import CSVImporter, {
  type CSVColumn,
} from '../../components/Elements/ReturnsDHLTrackingCSVImporter'

/**
 * Utils
 */

const mappedReturns = (returns: PaginatedReturn[]): Return[] => {
  return returns.map(ret => {
    return {
      address: ret.address
        ? {
            ...ret.address,
            streetAddress: ret.address.streetAddress || null,
            zip: ret.address.zip || null,
            city: ret.address.city || null,
          }
        : null,
      customer: {
        encodedPublicId: ret.owner.encodedPublicId,
      },
      documents: ret.documents.map(document => {
        return {
          id: document.id,
          metadata: document.scanMetadata || null,
          // documents can be returned only if they are in a scan center
          scanCenter: document.scanCenter,
        }
      }),
      finishedBy: ret.finishedBy,
      id: ret.id,
      requestedAt: ret.requestedAt,
      requesterUser: {
        email: ret.requesterUser.email || '',
      },
      sentAt: ret.sentAt,
    }
  })
}

const saveReturnsCSV = (
  scanCenter: ActiveScanCenterCodeEnum,
  returns: PaginatedReturn[]
) => {
  const csv = getReturnsCSV(mappedReturns(returns), scanCenter)
  // A return only makes it into the CSV when it holds a document with a scan id
  // in this scan center, so an empty string means there is nothing to pick.
  if (!csv) {
    notification.warning({
      message: i18n.t('returns.emptyCsv.title'),
      description: i18n.t('returns.emptyCsv.message'),
    })
    return
  }
  saveAs(new Blob([csv]), getReturnsCSVFileName(scanCenter))
}

/**
 * Component
 */

const ReturnsTable = (props: TableProps<PaginatedReturn>) => (
  <Table<PaginatedReturn> {...props} />
)

interface State {
  sending: { [id: string]: boolean }
  finishedBy: ContainerType[]
}

notification.config({})

const ReturnsRoute = () => {
  const { t } = useTranslation()
  const [state, setState] = useState<State>({
    sending: {},
    finishedBy: [ContainerType.ReturnImmediate],
  })
  const [sendReturn] = useMutation(SendReturnDocument, {
    update: (cache, { errors, data }) => {
      if (errors || !data) {
        return
      }
      const returnId = data.sendReturn.id
      const queryData = cache.readQuery({
        query: RequestedReturnsDocument,
        variables: returnsQuery.variables,
      }) as RequestedReturnsQuery
      if (!queryData || !queryData.containersConnection) {
        return
      }

      const { containersConnection } = queryData
      const index = containersConnection.edges.findIndex(
        edge =>
          edge.node.__typename === 'ContainerReturn' &&
          edge.node.id === returnId
      )
      if (index === -1) {
        return
      }

      const newEdges = [...containersConnection.edges]
      newEdges.splice(index, 1)

      cache.writeQuery({
        query: RequestedReturnsDocument,
        data: {
          __typename: queryData.__typename,
          containersConnection: {
            ...containersConnection,
            edges: newEdges,
            aggregate: {
              ...containersConnection.aggregate,
              count: containersConnection.aggregate.count - 1,
            },
          },
        },
        variables: returnsQuery.variables,
      })
    },
  })
  const [sendReturnsWithTrackingIds] = useMutation(
    SendReturnsWithTrackingIdsDocument
  )
  const returnsQuery = usePaginatedReturns({
    type_in: state.finishedBy,
    metadataReturn: {
      sentAt: null,
    },
  })
  const { loading, returns, next } = returnsQuery
  const columns: TableColumnProps<PaginatedReturn>[] = [
    {
      key: 'id',
      title: 'Return ID',
      render: (_, record) => <small>{record.id}</small>,
    },
    {
      key: 'publicId',
      title: 'Public ID',
      render: (_, record) => <small>{record.owner.encodedPublicId}</small>,
    },
    {
      key: 'receiver',
      title: i18n.t('returns.receiver'),
      render: (_, record) => {
        const address = record.address || defaultAddress
        if (!address) {
          return ''
        }
        const [name = ''] = address.lines
        return name
      },
    },
    {
      key: 'address',
      title: i18n.t('misc.address'),
      render: (_, record) => {
        const address = record.address || defaultAddress
        if (!address) {
          return ''
        }
        return `${address.streetAddress}, ${address.zip} ${address.city}`
      },
    },
    {
      key: 'country',
      title: i18n.t('misc.country'),
      render: (_, record) => {
        const address = record.address
        if (!address) {
          return ''
        }
        return address.countryCode
      },
    },
    {
      key: 'count',
      title: i18n.t('returns.count'),
      render(_, record) {
        const documents = record.documents || []
        return documents.length
      },
    },
    {
      key: 'type',
      dataIndex: 'type',
      filteredValue: state.finishedBy,
      filters: [
        {
          text: i18n.t('misc.month'),
          value: ContainerType.ReturnScheduled,
        },
        {
          text: i18n.t('misc.user'),
          value: ContainerType.ReturnImmediate,
        },
      ],
      title: i18n.t('returns.finishedBy'),
    },
    {
      key: 'requestedAt',
      title: i18n.t('misc.requestedAt'),
      render: (_, record) => record.requestedAt || '',
    },
    {
      key: 'actions',
      title: i18n.t('returns.actions'),
      render: (_, record) => (
        <Button
          type="primary"
          loading={state.sending[record.id]}
          onClick={async () => {
            const { id } = record
            setState({ ...state, sending: { ...state.sending, [id]: true } })
            await sendReturn({ variables: { id } })
            setState({ ...state, sending: { ...state.sending, [id]: false } })
          }}
        >
          {i18n.t('misc.send')}
        </Button>
      ),
    },
  ]
  return (
    <div>
      {/* CSV exports */}
      <div style={styles.exports}>
        <Button
          disabled={loading}
          type="primary"
          onClick={() => saveReturnsCSV(ActiveScanCenterCodeEnum.SPS, returns)}
        >
          {t('returns.returnsCsv.Sps')}
        </Button>
        <Button
          disabled={loading}
          type="primary"
          onClick={() => saveReturnsCSV(ActiveScanCenterCodeEnum.SRZ, returns)}
        >
          {t('returns.returnsCsv.Srz')}
        </Button>
        <Button
          disabled={loading}
          type="primary"
          onClick={() => saveReturnsCSV(ActiveScanCenterCodeEnum.ODS, returns)}
        >
          {t('returns.returnsCsv.Ods')}
        </Button>

        <CSVImporter
          onImport={async (columns: CSVColumn[]) => {
            try {
              await sendReturnsWithTrackingIds({
                variables: {
                  data: columns.map(({ returnId, trackingId }) => ({
                    returnShipmentId: returnId,
                    trackingId,
                  })),
                },
              })
              notification.info({
                message: i18n.t('returns.importingCompleted'),
                description: i18n.t('returns.dhlTrackingUploadCompletedMsg'),
              })
              returnsQuery.refetch()
            } catch (error) {
              notification.error({
                message: 'Uh oh.',
                description:
                  error.message || i18n.t('returns.somethingWentWrongMsg'),
                duration: 0,
              })
            }
          }}
        />
      </div>

      {/* Returns list */}
      <ReturnsTable
        rowKey="id"
        loading={loading}
        columns={columns}
        locale={{ emptyText: t('returns.noReturns') }}
        dataSource={returns}
        expandedRowRender={(item: PaginatedReturn) => {
          const documents = item.documents || []
          const documentColumns: TableColumnProps<ReturnDocument>[] = [
            {
              key: 'sender',
              title: i18n.t('returns.sender'),
              render: (_, { senderMetadata }) => senderMetadata?.name,
            },
            {
              key: 'subject',
              title: i18n.t('returns.subject'),
              dataIndex: 'subject',
            },
            {
              key: 'date',
              title: i18n.t('returns.date'),
              render: (_, record) =>
                dayjs(record.createdAt).format('DD.MM.YYYY'),
            },
          ]
          return (
            <Table
              rowKey="id"
              columns={documentColumns}
              locale={{ emptyText: i18n.t('returns.noDocuments') }}
              dataSource={documents}
              pagination={false}
              showHeader={!!documents.length}
            />
          )
        }}
        onChange={(_, filters) => {
          setState({ ...state, finishedBy: filters.type as ContainerType[] })
        }}
        pagination={false}
        defaultExpandAllRows={false}
      />
      {next && <Button onClick={() => next()}>{t('returns.loadMore')}</Button>}
    </div>
  )
}
const styles: InlineStyles = {
  exports: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
    marginBottom: 16,
  },
}

export default ReturnsRoute
